import React, { useCallback, useEffect, useRef, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Lenis from "lenis";
import axios from "axios";
import Preloader from "@/components/Preloader";
import Nav from "@/components/Nav";
import Hero from "@/components/Hero";
import Countdown from "@/components/Countdown";
import Marquee from "@/components/Marquee";
import Story from "@/components/Story";
import Celebrations from "@/components/Celebrations";
import Details from "@/components/Details";
import Rsvp from "@/components/Rsvp";
import Footer from "@/components/Footer";
import Cursor from "@/components/Cursor";
import { PalaceArch, FloralDivider } from "@/components/Ornaments";
import Engagement from "@/pages/Engagement";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHASE = process.env.REACT_APP_PHASE || "engagement";

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { err: false };
  }
  static getDerivedStateFromError() {
    return { err: true };
  }
  render() {
    if (this.state.err) {
      return <div className="eb-fallback serif">Something went wrong.</div>;
    }
    return this.props.children;
  }
}

function Home() {
  const lenisRef = useRef(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const lenis = new Lenis({ duration: 1.25, smoothWheel: true });
    lenis.stop();
    lenisRef.current = lenis;
    let raf;
    const loop = (t) => {
      lenis.raf(t);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      lenisRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (ready && lenisRef.current) lenisRef.current.start();
  }, [ready]);

  const scrollTo = useCallback((target) => {
    if (lenisRef.current) {
      lenisRef.current.scrollTo(target, { duration: 1.6 });
    } else {
      document.querySelector(target)?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  return (
    <>
      <Cursor />
      <Preloader onDone={() => setReady(true)} />
      <Nav onNav={scrollTo} ready={ready} />
      <main>
        <Hero ready={ready} onScrollCue={() => scrollTo("#countdown")} />
        <Countdown />
        <Marquee />
        <Story />
        <Celebrations />
        <Details />
        <Rsvp />
      </main>
      <Footer />
      <div className="grain" aria-hidden="true" />
    </>
  );
}

function HostPage() {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await axios.get(`${API}/rsvp`);
      setRows(r.data);
    } catch {
      setError("Could not load responses. Please try again.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const list = rows || [];
  const attending = list.filter((r) => r.attending === "yes");
  const guestCount = attending.reduce((s, r) => s + (Number(r.guests) || 1), 0);

  return (
    <div className="host-page" data-testid="host-page">
      <div className="host-frame" aria-hidden="true" />
      <header className="host-head">
        <PalaceArch width={92} />
        <div className="kicker host-kicker">Private · For the hosts</div>
        <h1 className="host-title serif">Guest Responses</h1>
        <div className="host-divider">
          <FloralDivider width={200} flowerFill="#f8f3ea" />
        </div>
      </header>

      <section className="host-stats" data-testid="host-stats">
        <div className="host-stat">
          <span className="host-stat-num serif" data-testid="host-total">
            {list.length}
          </span>
          <span className="host-stat-label">Responses</span>
        </div>
        <div className="host-stat">
          <span className="host-stat-num serif" data-testid="host-attending">
            {attending.length}
          </span>
          <span className="host-stat-label">Joyfully joining</span>
        </div>
        <div className="host-stat">
          <span className="host-stat-num serif" data-testid="host-guest-count">
            {guestCount}
          </span>
          <span className="host-stat-label">Total guests</span>
        </div>
      </section>

      <div className="host-actions">
        <button className="host-refresh" data-testid="host-refresh-button" onClick={load}>
          {loading ? "Refreshing…" : "Refresh"} <span>↻</span>
        </button>
        <a className="host-back" href="/">
          ← Back to invitation
        </a>
      </div>

      {error && (
        <div className="host-error" data-testid="host-error-message">
          {error}
        </div>
      )}

      {loading && !rows ? (
        <div className="host-empty serif" data-testid="host-loading">
          Gathering your responses…
        </div>
      ) : list.length === 0 ? (
        <div className="host-empty serif" data-testid="host-empty-state">
          No responses yet — they will appear here the moment a guest sends
          their RSVP.
        </div>
      ) : (
        <ul className="host-list">
          {list.map((r) => (
            <li className="host-row" key={r.id} data-testid="host-rsvp-row">
              <div className="host-row-main">
                <span className="host-row-name serif">{r.name}</span>
                <span
                  className={`host-pill${r.attending === "yes" ? " yes" : " no"}`}
                >
                  {r.attending === "yes" ? "Joyfully accepts" : "Regretfully declines"}
                </span>
              </div>
              <div className="host-row-meta">
                <span>{r.email}</span>
                {r.phone && <span>· {r.phone}</span>}
                <span>
                  · {Number(r.guests)} {Number(r.guests) === 1 ? "guest" : "guests"}
                </span>
                <span>
                  ·{" "}
                  {new Date(r.created_at).toLocaleDateString("en-GB", {
                    day: "numeric",
                    month: "short",
                  })}
                </span>
              </div>
              {r.message && (
                <p className="host-row-msg serif">&ldquo;{r.message}&rdquo;</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Routes>
          {/* The public link always points at "/". Which invitation it shows
              is controlled by REACT_APP_PHASE in .env ("engagement" now,
              flip to "wedding" before 26 November and redeploy — the link
              guests already have simply becomes the wedding invite). */}
          <Route
            path="/"
            element={PHASE === "wedding" ? <Home /> : <Engagement />}
          />
          <Route path="/engagement" element={<Navigate to="/" replace />} />
          <Route path="/wedding" element={<Home />} />
          <Route path="/host" element={<HostPage />} />
        </Routes>
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
